import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, Link } from '../../app/router';
import type { Org } from '../../lib/api';
import { Button, linkClass } from '../../ui';
import { auditLabel } from './audit-labels';

const PAGE = 50;

/** Sensitive events of the organization (spec F.1 `audit_log`, admins only by RLS), newest first, 50 at a time. */
export function AuditLogPage({ org }: { org: Org }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const allowed = org.role !== 'member';
  const log = useInfiniteQuery({
    queryKey: ['audit', org.id],
    enabled: allowed,
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => api.auditLog(org.id, { before: pageParam, limit: PAGE }),
    getNextPageParam: (last) => (last.length < PAGE ? undefined : last[last.length - 1]!.id),
  });
  const entries = log.data?.pages.flat() ?? [];

  return (
    <>
      <header className="flex flex-col gap-1">
        <Link to={href('settings')} className={linkClass}>
          ← {t('dashboard.settings')}
        </Link>
        <h1 className="font-display text-display-md font-normal tracking-display text-fg">{t('audit.title')}</h1>
      </header>
      {!allowed ? (
        <p className="font-sans text-fg-muted">{t('audit.forbidden')}</p>
      ) : (
        <>
          {log.isSuccess && entries.length === 0 && <p className="font-sans text-fg-muted">{t('audit.empty')}</p>}
          {entries.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full font-sans text-sm">
                <thead>
                  <tr className="text-left text-fg-muted">
                    <th className="py-1 pr-4 font-normal">{t('audit.date')}</th>
                    <th className="py-1 pr-4 font-normal">{t('audit.actor')}</th>
                    <th className="py-1 pr-4 font-normal">{t('audit.action')}</th>
                    <th className="py-1 font-normal">{t('audit.target')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-t border-border">
                      <td className="whitespace-nowrap py-1 pr-4 font-mono text-xs">{new Date(e.createdAt).toLocaleString(i18n.language)}</td>
                      <td className="py-1 pr-4">{e.actorName || t('audit.system')}</td>
                      <td className="py-1 pr-4">{auditLabel(e.action, t)}</td>
                      <td className="max-w-48 truncate py-1 font-mono text-xs text-fg-muted">{e.target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {log.hasNextPage && (
            <Button className="self-start" disabled={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
              {t('audit.older')}
            </Button>
          )}
        </>
      )}
    </>
  );
}
